import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Lang = 'zh' | 'en' | 'ja';

interface LanguageContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  /** Pick text by current language: t(zh, en, ja) */
  t: (zh: string, en: string, ja?: string) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: 'zh',
  setLang: () => {},
  t: (zh) => zh,
});

const STORAGE_KEY = 'polyq_lang';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'zh' || stored === 'ja') return stored as Lang;
    } catch {
      // ignore storage errors
    }
    return 'zh';
  });

  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  };

  const t = (zh: string, en: string, ja?: string): string => {
    if (lang === 'en') return en;
    if (lang === 'ja') return ja ?? en;
    return zh;
  };

  useEffect(() => {
    const map: Record<Lang, string> = { zh: 'zh-TW', en: 'en', ja: 'ja' };
    document.documentElement.lang = map[lang];
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
